/**
 * Native microphone permission handling.
 *
 * On the web we rely on the browser's getUserMedia prompt. On native iOS/Android
 * the WebView's getUserMedia ONLY works if the native shell has already been
 * granted microphone permission. We use the Capacitor speech-recognition
 * plugin (which ships a simple requestPermissions() we can co-opt) to drive
 * the native mic-permission dialog before any recording starts.
 *
 * Flow on native:
 *   1. Check current permission status.
 *   2. If undetermined → request.
 *   3. If denied → surface a friendly error pointing the user to Settings.
 *
 * Flow on web: this is a no-op; returns 'granted' so the browser prompt
 * inside getUserMedia takes over as before.
 */
import { isNative } from './native';

let _pluginCache = null;

async function getPlugin() {
  if (_pluginCache) return _pluginCache;
  try {
    // Lazy import so web bundles don't pull in the native plugin at all.
    const mod = await import('@capacitor-community/speech-recognition');
    _pluginCache = mod.SpeechRecognition;
    return _pluginCache;
  } catch {
    return null;
  }
}

/**
 * Ensure the native mic permission is granted. Returns one of:
 *   'granted' | 'denied' | 'not-native'
 */
export async function ensureMicPermission() {
  if (!isNative()) return 'not-native';
  const plugin = await getPlugin();
  if (!plugin) return 'granted'; // plugin missing in web build — trust WebView.
  try {
    const { permission } = await plugin.checkPermissions();
    if (permission === 'granted') return 'granted';
    if (permission === 'denied') return 'denied';
    const req = await plugin.requestPermissions();
    return req.permission === 'granted' ? 'granted' : 'denied';
  } catch {
    // If the plugin API fails, fall back to trusting getUserMedia's own error.
    return 'granted';
  }
}
